import { MediaFileStatus } from "@next-platform/packages-ts-types";
const BYTES_PER_KIBIBYTE = 1024;
const BYTES_PER_MEBIBYTE = BYTES_PER_KIBIBYTE * BYTES_PER_KIBIBYTE;
const MILLISECONDS_PER_SECOND = 1000;
function isLiveMediaFile(file) {
    return file.status !== MediaFileStatus.DELETED && file.status !== MediaFileStatus.EXPIRED;
}
function formatFileSize(sizeBytes) {
    if (sizeBytes >= BYTES_PER_MEBIBYTE) {
        return `${(sizeBytes / BYTES_PER_MEBIBYTE).toFixed(1)} MB`;
    }
    if (sizeBytes >= BYTES_PER_KIBIBYTE) {
        return `${(sizeBytes / BYTES_PER_KIBIBYTE).toFixed(1)} KB`;
    }
    return `${sizeBytes} B`;
}
function formatConnections(connections) {
    return connections.map((connection) => `${connection.type}:${connection.id}`);
}
/**
 * Turns a media-file record into metadata rows. `kind` maps to an i18n label in
 * the view. Most values are already display strings; `createdAt` converts the
 * API's Unix seconds to epoch milliseconds for the view's date formatter.
 * Connections stay as one row with many values. Tombstones yield no rows — missing or
 * expired placeholder copy belongs to the details placeholder milestone, not
 * this helper.
 */
export default function buildMediaFileMetadataRows(file) {
    if (!isLiveMediaFile(file)) {
        return [];
    }
    const rows = [];
    // The backend sends null until it can resolve the model's public name: no row rather than an empty one.
    if (file.model !== null) {
        rows.push({ kind: "model", values: [file.model] });
    }
    rows.push({ kind: "dimensions", values: [`${file.width}×${file.height}`] }, { kind: "size", values: [formatFileSize(file.size_bytes)] }, { kind: "source", values: [file.source] }, { kind: "visibility", values: [file.visibility] });
    const createdAtMilliseconds = file.createdAt * MILLISECONDS_PER_SECOND;
    if (Number.isFinite(file.createdAt) && Number.isFinite(createdAtMilliseconds)) {
        rows.push({ kind: "createdAt", values: [String(createdAtMilliseconds)] });
    }
    if (file.used_as.length > 0) {
        rows.push({ kind: "usedAs", values: [...file.used_as] });
    }
    if (file.tags.length > 0) {
        rows.push({ kind: "tags", values: [...file.tags] });
    }
    // Owner-only fields are independent — do not gate prompt on connections.
    if ("connections" in file && Array.isArray(file.connections) && file.connections.length > 0) {
        rows.push({ kind: "connections", values: formatConnections(file.connections) });
    }
    if ("prompt" in file && typeof file.prompt === "string" && file.prompt.length > 0) {
        rows.push({ kind: "prompt", values: [file.prompt] });
    }
    return rows;
}
